import { Dayjs } from '@dw/dayjs';

/**
 * How a class file is presented.
 *
 * Mirrors the API's `ClassFileMediaKind` BY NAME — the API serializes enums as their PascalCase
 * name — and matches the mobile app's `CLASS_FILE_MEDIA_KIND` exactly, so the same file looks like
 * the same kind of thing on both surfaces. Adding a value later is safe; removing or reordering one
 * is not.
 */
export enum CLASS_FILE_MEDIA_KIND {
  DOCUMENT = 'Document',
  IMAGE = 'Image',
  VIDEO = 'Video',
  AUDIO = 'Audio'
}

/** One file a studio has shared against a class. */
export interface IDanceClassFile {
  fileId: string;
  danceClassId?: string;
  /** Shared by every file added in one upload. The list GROUPS by this. */
  uploadBatchId?: string;
  description?: string;
  fileName?: string;
  contentType?: string;
  mediaKind?: CLASS_FILE_MEDIA_KIND;
  sizeInBytes?: number;
  /**
   * A SHORT-LIVED signed URL minted alongside the list, so a grid paints in one round trip. It
   * expires in about fifteen minutes — never persist it, and let the query go stale rather than
   * caching it long.
   */
  thumbnailUrl?: string;
  durationSeconds?: number;
  uploadedByUserId?: string;
  /** Empty when the staff member has since left the school. */
  uploadedByName?: string;
  createdTimestamp?: Dayjs;
}

export interface ICreateDanceClassFileItem {
  blobKey: string;
  thumbnailBlobKey?: string;
  durationSeconds?: number;
}

export interface ICreateDanceClassFilesRequest {
  description: string;
  files: ICreateDanceClassFileItem[];
}

/**
 * Checked in the browser so an oversize file is refused before it is uploaded. These must stay
 * identical to `ClassFileConstants` on the API and `CLASS_FILE_LIMITS` in the mobile app.
 */
export const CLASS_FILE_LIMITS = {
  MaxFilesPerUpload: 20,
  MaxVideoSizeBytes: 500 * 1024 * 1024,
  MaxOtherFileSizeBytes: 50 * 1024 * 1024,
  MaxThumbnailSizeBytes: 2 * 1024 * 1024,
  MaxDescriptionLength: 500
} as const;
